import {
  cosmicSupportedCurrencies,
  cosmicSupportedLocales,
} from '@taxbit-private/cosmic-localization'
import { z } from 'zod'

export enum AutomatedCalcCadence {
  Never = 'Never',
  ManualOnly = 'ManualOnly',
  DailyFull = 'DailyFull',
  Monthly = 'Monthly',
}

export const AUTOMATED_CALC_CADENCE_OPTIONS = [
  { label: 'Never', value: AutomatedCalcCadence.Never },
  { label: 'Manual Only', value: AutomatedCalcCadence.ManualOnly },
  { label: 'Daily Full', value: AutomatedCalcCadence.DailyFull },
  { label: 'Monthly', value: AutomatedCalcCadence.Monthly },
] as const

export enum AccountingMethod {
  UniversalFifo = 'UniversalFIFO',
  UniversalLifo = 'UniversalLIFO',
  UniversalHifo = 'UniversalHIFO',
  SourceFifo = 'SourceFIFO',
  SourceLifo = 'SourceLIFO',
  SourceFifoInternalCrossover = 'SourceFIFOInternalCrossover',
  SourceLifoInternalCrossover = 'SourceLIFOInternalCrossover',
  RetroactiveCostBasisFifo = 'RetroactiveCostBasisFIFO',
  WeightedAverageCost = 'WeightedAverageCost',
}

export const fetchOrgSettingsResponseSchema = z.object({
  explode_groups_experimental: z.boolean(),
  class_location_support: z.boolean(),
  enable_legal_entities: z.boolean(),
  netsuite_account_id: z.string().or(z.null()).optional(),
  netsuite_company_id: z.string().or(z.null()).optional(),
  source_start_date: z.string().or(z.null()).optional(),
  persist_timestamp_prices: z.boolean(),
  recompute_group_transaction_types: z.boolean(),
  accounting_method: z.nativeEnum(AccountingMethod).optional(),
  locale: z.enum(cosmicSupportedLocales).optional(),
  enable_incremental_sync: z.boolean().optional(),
  enable_automated_internal_transfer_detection: z.boolean().optional(),
  enable_automated_internal_transfer_modification: z.boolean().optional(),
  internal_transfer_detection_amount_volume_percentage: z.number().optional(),
  internal_transfer_detection_time_window_seconds: z.number().optional(),
  automated_spam_detection: z.boolean().optional(),
  persist_csv_source_information: z.boolean().optional(),
  use_optimized_transactions_table: z.boolean().optional(),
  use_atlas_reports: z.boolean().optional(),
  use_atlas_adjustment_reports: z.boolean().optional(),
  use_atlas_transactions_and_pricing: z.boolean().optional(),
  gainloss_chunk_days: z.number().or(z.null()).optional(),
  automated_calc_cadence: z.nativeEnum(AutomatedCalcCadence).optional(),
  emr_driver: z.string().or(z.null()).optional(),
  emr_executor: z.string().or(z.null()).optional(),
  // Valid option lists for select-based settings, supplied by the backend so the
  // source of truth stays in tactic-web (see app/main/atlas_emr_options.py).
  _options: z
    .object({
      emr_driver: z.array(z.string()),
      emr_executor: z.array(z.string()),
    })
    .optional(),
})

export type FetchOrgSettingsResponse = z.infer<typeof fetchOrgSettingsResponseSchema>

export type OrgSetting = keyof FetchOrgSettingsResponse

export const fetchOrgMetadataResponseSchema = z.object({
  organization: z.object({
    display_name: z.string().nullable(),
    id: z.string(),
    name: z.string(),
    parent: z.string().nullable(),
    pricing_setting: z.string().nullable(),
    settings: z.string().nullable(),
    slug: z.string().nullable(),
  }),
  integration_count: z.number().nullable(),
  integrations: z.array(z.string()).nullable().optional(),
  status: z.string(),
  type: z.string(),
})

export type FetchOrgMetadataResponse = z.infer<typeof fetchOrgMetadataResponseSchema>

export type OrgMetadata = keyof FetchOrgSettingsResponse

export enum PricingMarketProvider {
  Default = 'Default',
  Binance = 'Binance',
  BinanceUs = 'BinanceUs',
  Coinbase = 'Coinbase',
  CoinGecko = 'CoinGecko',
  CoinMarketCap = 'CoinMarketCap',
  Gemini = 'Gemini',
  Kraken = 'Kraken',
}

export const pricingMarketProviderLabelMap = {
  [PricingMarketProvider.Binance]: 'Binance',
  [PricingMarketProvider.BinanceUs]: 'Binance US',
  [PricingMarketProvider.Coinbase]: 'Coinbase',
  [PricingMarketProvider.CoinGecko]: 'CoinGecko',
  [PricingMarketProvider.CoinMarketCap]: 'CoinMarketCap',
  [PricingMarketProvider.Gemini]: 'Gemini',
  [PricingMarketProvider.Kraken]: 'Kraken',
  [PricingMarketProvider.Default]: 'Best price',
} as const

export const pricingMarketDropdownOptions = Object.values(PricingMarketProvider)
  .filter(
    (provider) =>
      provider !== PricingMarketProvider.Binance && provider !== PricingMarketProvider.BinanceUs
  )
  .map((provider) => ({
    value: provider,
    children: pricingMarketProviderLabelMap[provider],
  }))

export enum PricingMethod {
  Timestamp = 'Timestamp',
  DailyAverage = 'DailyAverage',
  PreviousDayAverage = 'PreviousDayAverage',
  BeginningOfDay = 'BeginningOfDay',
  PreviousBeginningOfDay = 'PreviousBeginningOfDay',
}

export const PRICING_METHOD = 'pricing_method'

export const pricingMethodLabelMap = {
  [PricingMethod.Timestamp]: 'Timestamp',
  [PricingMethod.DailyAverage]: 'Daily Average',
  [PricingMethod.PreviousDayAverage]: 'Previous Day Average',
  [PricingMethod.BeginningOfDay]: 'Beginning Of Day',
  [PricingMethod.PreviousBeginningOfDay]: 'Previous Beginning Of Day',
} as const

export const fetchPricingSettingsResponseSchema = z.object({
  result: z.object({
    id: z.string(),
    org: z.string(),
    primary_source: z.nativeEnum(PricingMarketProvider),
    secondary_source: z.nativeEnum(PricingMarketProvider),
    base_fiat_currency: z.enum(cosmicSupportedCurrencies),
    pricing_method: z.nativeEnum(PricingMethod),
  }),
})

export type FetchPricingSettingResponse = {
  result: {
    id: string
    org: string
    primary_source: PricingMarketProvider
    secondary_source: PricingMarketProvider
  }
}

export type UpdatePricingSettingRequest = {
  primary_source: PricingMarketProvider
  secondary_source: PricingMarketProvider
}

export type UpdatePricingMethodRequest = {
  pricing_method: PricingMethod
}

export const fetchPricingOverridesResponseSchema = z.object({
  result: z.array(
    z.object({
      asset: z.string(),
      provider: z.nativeEnum(PricingMarketProvider),
    })
  ),
})

export type FetchPricingOverridesResponse = z.infer<typeof fetchPricingOverridesResponseSchema>

const pricingOverrideUpdatedSchema = z.object({
  asset: z.string(),
  id: z.string().uuid(),
  org_price_setting: z.string().uuid(),
  provider: z.string(),
})

export const deletePricingOverrideResponseSchema = z.object({
  delete: pricingOverrideUpdatedSchema,
})

export const upsertPricingOverrideResponseSchema = z.object({
  result: pricingOverrideUpdatedSchema,
})

export type UpdatePricingOverrideRequest = {
  asset: string
  provider: PricingMarketProvider
}

export type RefreshPricesRequest = {
  refresh_details: {
    full_refresh: boolean
    asset: string | null
    date: string | null
  }
}

export const refreshPricesResponseSchema = z.object({
  created_timestamp: z.string().nullable(),
  id: z.string(),
  org: z.string(),
  refresh_details: z.object({
    asset: z.string().nullable(),
    date: z.string().nullable(),
    full_refresh: z.boolean(),
  }),
  run_id: z.string().nullable(),
  status: z.string(),
})
